import { ExternalLink, Video } from "lucide-react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { resolveVideoExamples } from "@produce/shared";

/**
 * Read-only "Video examples" card, as the crew will see it. Shared by
 * the wizard's review step and the review/publish and portal briefing
 * pages. Takes the raw `video_examples` Json or the editor's list, and
 * returns null when there is nothing to show, so callers can drop it in
 * unconditionally.
 */
export function VideoExamplesCard({
  videoExamples,
  className,
}: {
  videoExamples: unknown;
  className?: string;
}) {
  const examples = resolveVideoExamples(videoExamples);
  if (examples.length === 0) return null;
  return (
    <Card className={cn("mb-6 bg-card p-8", className)}>
      <div className="mb-4 flex items-center gap-2">
        <Video className="h-5 w-5 text-primary" />
        <h3 className="text-xl font-semibold">Video examples</h3>
      </div>
      <ul className="space-y-4">
        {examples.map((example) => (
          <li key={example.id}>
            <a
              href={example.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-semibold text-primary hover:underline"
            >
              {example.label} video
              <ExternalLink className="h-4 w-4" />
            </a>
            {example.startLabel && (
              <span className="ml-2 text-sm text-muted-foreground">{example.startLabel}</span>
            )}
            {example.note && (
              <p className="mt-1 whitespace-pre-wrap text-muted-foreground">{example.note}</p>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}
